import type { ScpData } from "@hobbyshopify/backend-client";
import { useEffect, useState } from "react";
import { GRADE_CATEGORIES, gradePriceText } from "../core/primitives/grades";
import { gradeLinkUrl } from "./ebay-links";
import { gradeGridHint, gradeGridTab } from "./grade-grid";
import { RefreshIcon } from "./review-icons";

// eBay grade links + per-grade prices, ported from the iOS CardDetailView
// (category selector + grade-button grid). Prices come from `scp_data`
// (SportsCardsPro, in cents); links from the `search_links` map. Buttons
// without a link render dimmed/non-clickable, mirroring `ebayButtonColor`.
//
// The taxonomy and price mapping live in lib/grades, shared with the
// grade field of the review screen and the card page.

// Opened verbatim: scan-core already appends the affiliate params when it builds
// `search_links` (see its pure/ebay-links.ts + constants.ts). Re-appending them
// here duplicated every mkcid/mkevt/campid pair, and eBay answers a link with
// duplicated tracking params by redirecting to sign-in — which is why these
// worked in the iOS app (it opens the URL as-is) but not on web.
function openLink(url?: string) {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
}

export function EbayGradeLinks({
  searchLinks,
  scpData,
  pricedGradeKey,
  cardGradeKey,
  cardTitle,
  onRefresh,
  refreshing = false,
  held = false,
}: {
  searchLinks?: Record<string, string>;
  scpData?: ScpData | null;
  /**
   * The grade the asking price was read from — `estimatedPriceGrade`, the
   * backend's own answer rather than a guess made from the amounts here.
   *
   * Marked because the estimate is often *not* this card's grade: SCP prices
   * PSA 1–10 and the top SGC/CGC grade, so a BGS slab or a half grade is read
   * off the PSA comp below it. A reviewer looking at a grid of twenty numbers
   * cannot tell which one became the figure in the form, and that is exactly
   * the thing they are being asked to approve.
   */
  pricedGradeKey?: string | null;
  /**
   * This card's own grade, as a key — `raw` for an ungraded card. Filled in the
   * grid, as iOS marks it, and the tab the grid opens on when the price has no
   * source in the grid.
   *
   * Separate from `pricedGradeKey` because the two answer different questions
   * and only agree sometimes: a BGS 9.5 is priced off the PSA 9 comp, and an
   * ungraded card with no loose price on SCP is priced off nothing at all. That
   * second case is why this exists — `pricedGradeKey` is null there, so a raw
   * card marked nothing and the selector fell back to reading PSA as current.
   */
  cardGradeKey?: string | null;
  /**
   * The card's title, when a grade without a stored link should search eBay by
   * it rather than dim — as the review row's quick links do, so the two agree.
   */
  cardTitle?: string;
  /** Looks the card's price up again: offered for a card that exists, as iOS offers it. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /** Held while another change on the card goes, which the page says. */
  held?: boolean;
}) {
  const linkFor = (key: string) =>
    cardTitle === undefined ? searchLinks?.[key] : (gradeLinkUrl(key, searchLinks, cardTitle) ?? undefined);
  // Open where a marked button is on screen without hunting for it.
  const [selected, setSelected] = useState(() => gradeGridTab(pricedGradeKey, cardGradeKey) ?? "psa");

  // And follow the marks when they move: correcting the grade to a BGS one while
  // the grid stays on the PSA tab would hide the mark behind a tab nobody has a
  // reason to open. A tab the reviewer chose themselves is left alone — this
  // only fires when a marked grade actually changes.
  useEffect(() => {
    const tab = gradeGridTab(pricedGradeKey, cardGradeKey);
    if (tab) setSelected(tab);
  }, [cardGradeKey, pricedGradeKey]);

  // RAW has no button in the grid below, so the tab is the only place an
  // ungraded card can be marked at all.
  const rawIsThisCard = cardGradeKey === "raw" || pricedGradeKey === "raw";

  const rawUrl = linkFor("raw");
  const rawPriceText = gradePriceText("raw", scpData);
  const activeCategory = GRADE_CATEGORIES.find((c) => c.id === selected) ?? GRADE_CATEGORIES[0];

  const tabBase = "flex-1 rounded-md px-2 py-1.5 text-center transition-colors disabled:cursor-default";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-white">eBay sold prices &amp; links</span>
        {onRefresh ? (
          // Held, not disabled, while it prices or another change goes: the focus stays on it.
          <button
            type="button"
            onClick={refreshing || held ? undefined : onRefresh}
            aria-disabled={refreshing || held || undefined}
            className="flex items-center gap-1.5 text-xs font-semibold text-accent aria-disabled:opacity-60"
          >
            <RefreshIcon size={12} />
            {refreshing ? "Pricing…" : "Re-price"}
          </button>
        ) : (
          <span className="text-[10px] text-faint">{gradeGridHint(pricedGradeKey, cardGradeKey)}</span>
        )}
      </div>
      {onRefresh && <p className="text-[10px] text-faint">{gradeGridHint(pricedGradeKey, cardGradeKey)}</p>}

      {/* Category selector — RAW links straight out, the rest swap the grid below. */}
      <div className="flex rounded-btn bg-surface p-1 text-xs font-medium">
        <button
          type="button"
          disabled={!rawUrl}
          onClick={() => openLink(rawUrl)}
          // RAW has no button in the grid below, so an ungraded card's price can
          // only be marked here.
          className={`${tabBase} ${
            rawIsThisCard ? "bg-accent/10 text-accent" : rawUrl ? "text-white hover:bg-surface-strong" : "text-faint"
          }`}
        >
          <span className="block">RAW</span>
          {rawPriceText && <span className="block text-[10px] font-normal">{rawPriceText}</span>}
        </button>
        {GRADE_CATEGORIES.map((cat) => {
          const isSelected = cat.id === selected;
          const priceText = cat.priceKey ? gradePriceText(cat.priceKey, scpData) : "";
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setSelected(cat.id)}
              className={`${tabBase} ${isSelected ? "bg-surface-strong text-accent" : "text-muted hover:text-white"}`}
            >
              <span className="block">{cat.title}</span>
              {priceText && <span className="block text-[10px] font-normal">{priceText}</span>}
            </button>
          );
        })}
      </div>

      {/* Grade grid for the selected category. */}
      <div className="grid grid-cols-3 gap-2">
        {activeCategory.grades.map((grade) => {
          const url = linkFor(grade.key);
          const priceText = gradePriceText(grade.key, scpData);
          const priced = grade.key === pricedGradeKey;
          const own = grade.key === cardGradeKey;
          return (
            <button
              key={grade.key}
              type="button"
              disabled={!url}
              onClick={() => openLink(url)}
              aria-current={own ? "true" : undefined}
              // The accent is the hover/hit affordance, since a web grid has no
              // persistent selection to mark — except for two facts about this
              // card: its own grade is filled, as iOS fills it, and the grade
              // the price was read from says so, tinted when it is another one.
              className={`rounded-btn border px-2 py-2 text-xs font-medium transition-colors ${
                own
                  ? "border-accent bg-accent text-ink"
                  : priced
                    ? "border-accent bg-accent/10 text-accent"
                    : url
                      ? "border-line text-white hover:border-accent hover:text-accent"
                      : "cursor-default border-line text-faint opacity-50"
              }`}
            >
              <span className="block">{grade.label}</span>
              {priceText && <span className="block text-[11px] font-normal">{priceText}</span>}
              {priced && <span className="block text-[10px] font-normal opacity-80">Price from here</span>}
            </button>
          );
        })}
      </div>

      <p className="text-[10px] italic text-faint">
        As an eBay Partner, we may earn commissions from purchases made through these links.
      </p>
    </div>
  );
}
